/**
 * User-friendly error handling for authentication failures
 * Provides consistent error messages and recovery strategies
 * Similar pattern to entitlementErrorHandler.ts
 */

import { createLogger } from './logger';

const authLogger = createLogger('AuthError');

export interface AuthError {
  code: string;
  message: string;
  userMessage: string;
  recoverable: boolean;
  retryable: boolean;
  actionRequired?: 'LOGIN' | 'RESEND_VERIFICATION' | 'RETRY' | 'CONTACT_SUPPORT';
}

export enum AuthErrorCode {
  CAPTCHA_FAILED = 'CAPTCHA_FAILED',
  CAPTCHA_TIMEOUT = 'CAPTCHA_TIMEOUT',
  CAPTCHA_INVALID = 'CAPTCHA_INVALID',
  EMAIL_EXISTS = 'EMAIL_EXISTS',
  EMAIL_UNCONFIRMED = 'EMAIL_UNCONFIRMED',
  WEAK_PASSWORD = 'WEAK_PASSWORD',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_EMAIL = 'INVALID_EMAIL',
  NETWORK_ERROR = 'NETWORK_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * User-friendly error messages for authentication failures
 */
const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, AuthError> = {
  [AuthErrorCode.CAPTCHA_FAILED]: {
    code: 'CAPTCHA_FAILED',
    message: 'Captcha validation failed',
    userMessage: 'The captcha challenge could not be verified. This might be due to a slow internet connection, VPN, or privacy tools. Please try again.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.CAPTCHA_TIMEOUT]: {
    code: 'CAPTCHA_TIMEOUT',
    message: 'Captcha validation timed out',
    userMessage: 'The captcha verification timed out. Please complete the captcha again and try submitting.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.CAPTCHA_INVALID]: {
    code: 'CAPTCHA_INVALID',
    message: 'Invalid captcha token',
    userMessage: 'The captcha token was invalid. Please refresh and try again.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.EMAIL_EXISTS]: {
    code: 'EMAIL_EXISTS',
    message: 'Email already registered',
    userMessage: 'An account with this email already exists. If this is your account, please try logging in instead.',
    recoverable: true,
    retryable: false,
    actionRequired: 'LOGIN'
  },
  [AuthErrorCode.EMAIL_UNCONFIRMED]: {
    code: 'EMAIL_UNCONFIRMED',
    message: 'Email exists but unconfirmed',
    userMessage: 'An account with this email exists but is not yet verified. Please check your email for the verification link or request a new one below.',
    recoverable: true,
    retryable: false,
    actionRequired: 'RESEND_VERIFICATION'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    code: 'WEAK_PASSWORD',
    message: 'Password does not meet requirements',
    userMessage: 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    code: 'INVALID_CREDENTIALS',
    message: 'Invalid email or password',
    userMessage: 'The email or password you entered is incorrect. Please try again.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    code: 'INVALID_EMAIL',
    message: 'Invalid email format',
    userMessage: 'Please enter a valid email address.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.NETWORK_ERROR]: {
    code: 'NETWORK_ERROR',
    message: 'Network connection failed',
    userMessage: 'Unable to connect to the authentication service. Please check your internet connection and try again.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.RATE_LIMIT_ERROR]: {
    code: 'RATE_LIMIT_ERROR',
    message: 'Too many requests',
    userMessage: 'Too many login attempts. Please wait a moment before trying again.',
    recoverable: true,
    retryable: true,
    actionRequired: 'RETRY'
  },
  [AuthErrorCode.UNKNOWN_ERROR]: {
    code: 'UNKNOWN_ERROR',
    message: 'Unknown error occurred',
    userMessage: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
    recoverable: true,
    retryable: true,
    actionRequired: 'CONTACT_SUPPORT'
  }
};

/**
 * Categorize errors from Supabase Auth
 */
export function categorizeAuthError(error: any): AuthError {
  // Handle captcha errors
  if (error?.message?.includes('captcha') || error?.code === 'captcha_failed') {
    if (error?.message?.includes('timeout')) {
      return AUTH_ERROR_MESSAGES[AuthErrorCode.CAPTCHA_TIMEOUT];
    }
    if (error?.message?.includes('invalid')) {
      return AUTH_ERROR_MESSAGES[AuthErrorCode.CAPTCHA_INVALID];
    }
    return AUTH_ERROR_MESSAGES[AuthErrorCode.CAPTCHA_FAILED];
  }

  // Handle email already registered
  if (error?.message?.includes('already registered') ||
      error?.message?.includes('already exists') ||
      error?.message?.includes('email_address_not_unique') ||
      error?.message?.includes('User already registered')) {
    return AUTH_ERROR_MESSAGES[AuthErrorCode.EMAIL_EXISTS];
  }

  // Handle weak password
  if (error?.message?.includes('weak password') ||
      (error?.message?.includes('password') && error?.message?.includes('requirement'))) {
    return AUTH_ERROR_MESSAGES[AuthErrorCode.WEAK_PASSWORD];
  }

  // Handle invalid credentials
  if (error?.message?.includes('Invalid login credentials') ||
      error?.message?.includes('invalid_credentials') ||
      error?.message?.includes('Email not confirmed')) {
    return AUTH_ERROR_MESSAGES[AuthErrorCode.INVALID_CREDENTIALS];
  }

  // Handle invalid email
  if (error?.message?.includes('invalid email') ||
      error?.message?.includes('email format')) {
    return AUTH_ERROR_MESSAGES[AuthErrorCode.INVALID_EMAIL];
  }

  // Handle network errors
  if (error?.message?.includes('fetch') ||
      error?.message?.includes('network') ||
      error?.code === 'NETWORK_ERROR' ||
      error?.name === 'NetworkError') {
    return AUTH_ERROR_MESSAGES[AuthErrorCode.NETWORK_ERROR];
  }

  // Handle rate limit errors
  if (error?.message?.includes('rate limit') ||
      error?.message?.includes('too many') ||
      error?.code === '429' ||
      error?.status === 429) {
    return AUTH_ERROR_MESSAGES[AuthErrorCode.RATE_LIMIT_ERROR];
  }

  // Default to unknown error
  return AUTH_ERROR_MESSAGES[AuthErrorCode.UNKNOWN_ERROR];
}

/**
 * Get recovery suggestions for authentication errors
 */
export function getRecoverySuggestions(error: AuthError): string[] {
  const suggestions: string[] = [];

  switch (error.code) {
    case AuthErrorCode.CAPTCHA_FAILED:
    case AuthErrorCode.CAPTCHA_TIMEOUT:
    case AuthErrorCode.CAPTCHA_INVALID:
      suggestions.push('Try disabling VPN or proxy if you\'re using one');
      suggestions.push('Disable browser extensions that might interfere with captcha');
      suggestions.push('Try refreshing the page and completing the captcha again');
      break;

    case AuthErrorCode.EMAIL_EXISTS:
      suggestions.push('Click "Try Login Instead" to sign in to your existing account');
      suggestions.push('Use "Forgot Password" if you don\'t remember your password');
      break;

    case AuthErrorCode.EMAIL_UNCONFIRMED:
      suggestions.push('Check your email inbox for the verification link');
      suggestions.push('Check your spam/junk folder');
      suggestions.push('Click "Resend Verification Email" to get a new link');
      break;

    case AuthErrorCode.WEAK_PASSWORD:
      suggestions.push('Use at least 8 characters');
      suggestions.push('Include uppercase and lowercase letters');
      suggestions.push('Add numbers and special characters (!@#$%^&*)');
      break;

    case AuthErrorCode.INVALID_CREDENTIALS:
      suggestions.push('Double-check your email and password');
      suggestions.push('Make sure Caps Lock is off');
      suggestions.push('Use "Forgot Password" if you need to reset');
      break;

    case AuthErrorCode.NETWORK_ERROR:
      suggestions.push('Check your internet connection');
      suggestions.push('Try refreshing the page');
      suggestions.push('Disable any VPN or proxy temporarily');
      break;

    case AuthErrorCode.RATE_LIMIT_ERROR:
      suggestions.push('Wait 30 seconds before trying again');
      suggestions.push('Avoid repeatedly clicking the submit button');
      break;

    default:
      suggestions.push('Refresh the page and try again');
      suggestions.push('Clear your browser cache');
      suggestions.push('Try using an incognito/private window');
      suggestions.push('Contact support if the problem continues');
  }

  return suggestions;
}

/**
 * Track authentication errors for monitoring
 */
export function trackAuthError(error: AuthError, context?: any): void {
  // Track error for analytics
  if (typeof window !== 'undefined' && (window as any).analytics?.track) {
    (window as any).analytics.track('auth_error', {
      error_code: error.code,
      error_message: error.message,
      user_message: error.userMessage,
      recoverable: error.recoverable,
      retryable: error.retryable,
      action_required: error.actionRequired,
      context,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href
    });
  }

  // Also log to console in development
  if (process.env.REACT_APP_ENV === 'development' && authLogger.isEnabled()) {
    authLogger.error('Authentication error', {
      error,
      context,
      timestamp: new Date().toISOString()
    });
  }
}

/**
 * Create a standardized error response for authentication failures
 */
export function createAuthErrorResponse(originalError: any, context?: any): {
  error: AuthError;
  suggestions: string[];
  timestamp: string;
} {
  const error = categorizeAuthError(originalError);
  const suggestions = getRecoverySuggestions(error);

  // Track the error
  trackAuthError(error, { originalError, context });

  return {
    error,
    suggestions,
    timestamp: new Date().toISOString()
  };
}

/**
 * Determine if an error should trigger a retry
 */
export function shouldRetryAuthError(error: AuthError): boolean {
  return error.retryable;
}

/**
 * Calculate retry delay with exponential backoff for auth errors
 */
export function calculateRetryDelay(attemptNumber: number, baseDelay: number = 1000): number {
  const maxDelay = 30000; // 30 seconds max
  const delay = baseDelay * Math.pow(2, attemptNumber - 1);
  return Math.min(delay, maxDelay);
}

const authErrorHandler = {
  AuthErrorCode,
  categorizeAuthError,
  getRecoverySuggestions,
  trackAuthError,
  createAuthErrorResponse,
  shouldRetryAuthError,
  calculateRetryDelay
};

export default authErrorHandler;
